import axios from 'axios'
import AuthService from './auth-header'

// This endpoint only exists on the production server — always hit the live URL
const ENDPOINT = 'https://ats.sattvahuman.in/api/dashboard/recruiter-performance'
const RAW_ENDPOINT = `${ENDPOINT}/raw`

function rawNotDeployedError() {
  const err = new Error('Raw extract not deployed')
  err.code = 'RAW_NOT_DEPLOYED'
  return err
}

/**
 * Fetch recruiter performance data for a date range and vertical.
 *
 * @param {string} startDate  'YYYY-MM-DD'
 * @param {string} endDate    'YYYY-MM-DD'
 * @param {string|null} jobVertical  'Infra Industry' | 'Service Industry' | null
 * @returns {Promise<Array>}  flat array of result records
 */
async function fetchVertical(startDate, endDate, jobVertical) {
  const params = { start_date: startDate, end_date: endDate }
  if (jobVertical) params.job_vertical = jobVertical

  const res = await axios.get(ENDPOINT, {
    headers: AuthService.authHeader(),
    params,
  })

  const body = res.data
  if (body?.status === 'OK' && Array.isArray(body?.data?.results)) {
    // Preserve any category/collar the API already returns. Infra Blue/White
    // split needs one of: job_category, category, collar, job_collar.
    // Service is treated as White collar in the UI even when that field is absent.
    return body.data.results.map(r => ({ ...r, job_vertical: jobVertical || 'Infra Industry' }))
  }
  throw new Error(body?.message || 'Failed to fetch performance data')
}

/**
 * Fetch event-level rows from /recruiter-performance/raw.
 * Does not invent rows from the summary — 404 means the extract is not deployed.
 *
 * @param {string} startDate
 * @param {string} endDate
 * @param {string|null} jobVertical
 * @param {string|null} month  optional YYYY-MM
 * @returns {Promise<Array>}
 */
async function fetchRawVertical(startDate, endDate, jobVertical, month) {
  const params = { start_date: startDate, end_date: endDate }
  if (jobVertical) params.job_vertical = jobVertical
  if (month) params.month = month

  let res
  try {
    res = await axios.get(RAW_ENDPOINT, {
      headers: AuthService.authHeader(),
      params,
    })
  } catch (e) {
    if (e?.response?.status === 404) throw rawNotDeployedError()
    const msg = e?.response?.data?.message || e.message || 'Failed to fetch raw performance data'
    const err = new Error(msg)
    err.code = e?.response?.status === 403 ? 'RAW_FORBIDDEN' : 'RAW_FAILED'
    throw err
  }

  const body = res.data
  if (body?.status === 'OK' && Array.isArray(body?.data?.results)) {
    return body.data.results
  }
  if (body?.status === 'FAILED') {
    const err = new Error(body?.message || 'Failed to fetch raw performance data')
    err.code = 'RAW_FAILED'
    throw err
  }
  throw new Error(body?.message || 'Failed to fetch raw performance data')
}

export default {
  /**
   * Fetch performance data.
   * vertical: 'Infra Industry' | 'Service Industry' | 'Both'
   * Returns all rows tagged with their job_vertical.
   */
  async getPerformanceData(startDate, endDate, vertical = 'Both') {
    if (vertical === 'Both') {
      const [infra, service] = await Promise.all([
        fetchVertical(startDate, endDate, 'Infra Industry'),
        fetchVertical(startDate, endDate, 'Service Industry'),
      ])
      return [...infra, ...service]
    }
    return fetchVertical(startDate, endDate, vertical)
  },

  /**
   * Fetch event-level raw rows (candidate × job × event_type).
   * Same start_date / end_date / job_vertical as getPerformanceData.
   * Optional month (YYYY-MM) is forwarded when the caller has one.
   */
  async getPerformanceRawData(startDate, endDate, vertical = 'Both', month = null) {
    if (vertical === 'Both') {
      const [infra, service] = await Promise.all([
        fetchRawVertical(startDate, endDate, 'Infra Industry', month),
        fetchRawVertical(startDate, endDate, 'Service Industry', month),
      ])
      return [...infra, ...service]
    }
    return fetchRawVertical(startDate, endDate, vertical, month)
  },
}
